/**
 * Buddy requests the member sent in this tab.
 *
 * GetOtherUserProfile reports `isBuddy` and `invited` (an invite waiting on the
 * viewer) but nothing for a request the viewer already sent, so a profile opened
 * after requesting someone from the Buddies page would offer "Request Buddy"
 * again. This is a UI hint only: the server's `isBuddy` always wins, and the
 * record is dropped once the request is accepted.
 */
const STORAGE_KEY = '@wlb/sent-buddy-requests';

const read = (): number[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
};

const write = (ids: number[]): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Private-mode or quota failures only cost the hint, never the request.
  }
};

const toId = (userId?: number | string | null): number | null => {
  const id = Number(userId);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export const rememberBuddyRequest = (userId?: number | string | null): void => {
  const id = toId(userId);
  if (id === null) return;
  const ids = read();
  if (!ids.includes(id)) write([...ids, id]);
};

export const forgetBuddyRequest = (userId?: number | string | null): void => {
  const id = toId(userId);
  if (id === null) return;
  const ids = read();
  if (ids.includes(id)) write(ids.filter(existing => existing !== id));
};

export const hasPendingBuddyRequest = (userId?: number | string | null): boolean => {
  const id = toId(userId);
  return id !== null && read().includes(id);
};
